import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudQueue as CloudIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { useAuth } from '../contexts/GoogleAuthContext';
import NotificationSnackbar from '../components/NotificationSnackbar';
import {
  type DiscoveryConfig
} from '../services/api';
import { api, Service } from '../services/api-client';

export default function ServiceDiscovery() {
  const { user } = useAuth();
  const canManagePermissions = user?.role === 'admin' || user?.role === 'developer';
  const queryClient = useQueryClient();
  const [addPrefixOpen, setAddPrefixOpen] = useState(false);
  const [addExcludeOpen, setAddExcludeOpen] = useState(false);
  const [newPrefix, setNewPrefix] = useState('');
  const [newExclude, setNewExclude] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Fetch discovery config
  const { data: config, isLoading: configLoading, error: configError } = useQuery(
    'discoveryConfig',
    api.getDiscoveryConfig,
    {
      refetchInterval: 60000, // Refresh every minute to show last refresh time
      retry: 2,
      onError: (error) => {
        console.error('Failed to fetch discovery config:', error);
      }
    }
  );

  // Fetch current services
  const { data: servicesData, isLoading: servicesLoading, error: servicesError, refetch: refetchServices } = useQuery(
    'services',
    () => api.getServices(),
    {
      retry: 2,
      onError: (error: Error) => {
        console.error('Failed to fetch services:', error);
      }
    }
  );
  const services = servicesData?.services || [];

  // Update config mutation
  const updateConfigMutation = useMutation(
    (newConfig: Partial<DiscoveryConfig>) => api.updateDiscoveryConfig(newConfig),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('discoveryConfig');
        setSnackbar({
          open: true,
          message: 'Discovery configuration updated',
          severity: 'success'
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: 'Failed to update configuration',
          severity: 'error'
        });
      }
    }
  );

  // Manual refresh mutation
  const refreshMutation = useMutation(
    api.triggerManualRefresh,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('services');
        queryClient.invalidateQueries('discoveryConfig');
        setSnackbar({
          open: true,
          message: `Refresh complete! Found ${data.servicesFound} services`,
          severity: 'success'
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: 'Failed to refresh services',
          severity: 'error'
        });
      }
    }
  );

  const handleAddPrefix = () => {
    if (!newPrefix || !config) return;

    const updatedPrefixes = [...config.prefixes, newPrefix];
    updateConfigMutation.mutate({ prefixes: updatedPrefixes });
    setNewPrefix('');
    setAddPrefixOpen(false);
  };

  const handleRemovePrefix = (prefix: string) => {
    if (!config) return;

    const updatedPrefixes = config.prefixes.filter((p: string) => p !== prefix);
    updateConfigMutation.mutate({ prefixes: updatedPrefixes });
  };

  const handleAddExclude = () => {
    if (!newExclude || !config) return;

    const updatedExcludes = [...config.excludePatterns, newExclude];
    updateConfigMutation.mutate({ excludePatterns: updatedExcludes });
    setNewExclude('');
    setAddExcludeOpen(false);
  };

  const handleRemoveExclude = (pattern: string) => {
    if (!config) return;

    const updatedExcludes = config.excludePatterns.filter((p: string) => p !== pattern);
    updateConfigMutation.mutate({ excludePatterns: updatedExcludes });
  };

  const handleToggleAutoDiscover = () => {
    if (!config) return;

    updateConfigMutation.mutate({ autoDiscoverEnabled: !config.autoDiscoverEnabled });
  };

  const getTimeSinceRefresh = () => {
    if (!config?.lastRefresh) return 'Never';

    const minutes = Math.floor((Date.now() - new Date(config.lastRefresh).getTime()) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    return `${minutes} minutes ago`;
  };

  if (configLoading || servicesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasErrors = configError || servicesError;
  const isUsingMockData = services?.some((s: { id?: string }) => s.id?.includes('mock'));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4">Service Discovery</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage which services are available in the Service Registry
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={refreshMutation.isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isLoading || !canManagePermissions}
        >
          Refresh Now
        </Button>
      </Box>

      {!canManagePermissions && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access. Contact an admin to make changes.
        </Alert>
      )}

      {hasErrors && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">Service Registry Connection Issue</Typography>
          <Typography variant="body2">
            Unable to connect to the service registry. Showing cached data.
            {servicesError && ` Error: ${(servicesError as Error).message}`}
          </Typography>
        </Alert>
      )}

      {isUsingMockData && !hasErrors && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">Demo Mode</Typography>
          <Typography variant="body2">
            Currently showing demo data. The service registry will be populated with real services once they are deployed.
          </Typography>
        </Alert>
      )}

      {/* Discovery Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Discovery Status</Typography>
            <Chip
              icon={config?.autoDiscoverEnabled ? <CheckCircleIcon /> : <ErrorIcon />}
              label={config?.autoDiscoverEnabled ? 'Auto-Discovery Enabled' : 'Auto-Discovery Disabled'}
              color={config?.autoDiscoverEnabled ? 'success' : 'default'}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Refresh
                  </Typography>
                  <Typography variant="body1">
                    {getTimeSinceRefresh()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Services Found
                  </Typography>
                  <Typography variant="body1">
                    {services?.length || 0} services
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RefreshIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Refresh Interval
                  </Typography>
                  <Typography variant="body1">
                    Every {config?.refreshInterval} minutes
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {canManagePermissions && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleToggleAutoDiscover}
                color={config?.autoDiscoverEnabled ? 'error' : 'success'}
              >
                {config?.autoDiscoverEnabled ? 'Disable Auto-Discovery' : 'Enable Auto-Discovery'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Discovery Prefixes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Service Name Prefixes</Typography>
            {canManagePermissions && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddPrefixOpen(true)}
              >
                Add Prefix
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Only Cloud Run services with names starting with these prefixes will be included in the registry
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {config?.prefixes.map((prefix: string) => (
              <Chip
                key={prefix}
                label={prefix}
                onDelete={canManagePermissions ? () => handleRemovePrefix(prefix) : undefined}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>

          {config?.prefixes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No prefixes configured. All Cloud Run services will be discovered.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Exclude Patterns */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Exclude Patterns</Typography>
            {canManagePermissions && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddExcludeOpen(true)}
              >
                Add Pattern
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Cloud Run services matching these patterns will be excluded from the registry (supports wildcards: *)
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {config?.excludePatterns.map((pattern: string) => (
              <Chip
                key={pattern}
                label={pattern}
                onDelete={canManagePermissions ? () => handleRemoveExclude(pattern) : undefined}
                color="error"
                variant="outlined"
              />
            ))}
          </Box>

          {config?.excludePatterns.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No exclude patterns configured.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Currently Discovered Services */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Services in Registry
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            These are the services currently available in the Service Registry that agents can access
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>URL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services && services.length > 0 ? (
                  services.map((service: Service) => (
                    <TableRow key={service.id || service.name}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {service.name || service.id}
                        </Typography>
                        {service.description && (
                          <Typography variant="caption" color="text.secondary">
                            {service.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={service.type || 'api'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={service.runtime?.health_status === 'healthy' ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={service.runtime?.health_status || (service.active ? 'active' : 'inactive')}
                          size="small"
                          color={service.runtime?.health_status === 'healthy' ? 'success' : service.runtime?.health_status === 'unhealthy' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {service.updated_at ? format(new Date(service.updated_at), 'MMM d, h:mm a') : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={service.url}>
                          <Typography variant="caption" sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }}>
                            {service.url}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {hasErrors ? 'Unable to load services' : 'No services discovered yet. Click "Refresh Now" to discover services.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Prefix Dialog */}
      <Dialog open={addPrefixOpen} onClose={() => setAddPrefixOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Service Prefix</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Prefix"
            value={newPrefix}
            onChange={(e) => setNewPrefix(e.target.value)}
            placeholder="e.g., mcp-, agent-"
            helperText="Services starting with this prefix will be auto-discovered"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPrefixOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddPrefix}
            disabled={!newPrefix}
          >
            Add Prefix
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Exclude Pattern Dialog */}
      <Dialog open={addExcludeOpen} onClose={() => setAddExcludeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Exclude Pattern</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Pattern"
            value={newExclude}
            onChange={(e) => setNewExclude(e.target.value)}
            placeholder="e.g., *-test, *-dev"
            helperText="Services matching this pattern will be excluded"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExcludeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddExclude}
            disabled={!newExclude}
          >
            Add Pattern
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}
